import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, TrendingUp, Users, CheckCircle, Filter, Layers, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
  BarChart, Bar, Cell,
} from "recharts";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_LABELS = {
  platforms: "Gig platforms (multi-select)",
  platform: "Primary platform",
  state: "State",
  city: "City",
  frequency: "Work frequency",
  earnings_range: "Weekly earnings",
  ideal_advance: "Ideal advance",
  top_challenge: "Top challenge",
  urgency: "Timing preference",
  source: "How they heard",
  gap_frequency: "Gap frequency",
  usage_frequency: "Expected usage",
  membership_interest: "Membership interest",
  beta_interest: "Beta access interest",
  interview_interest: "Open to interview?",
  device_type: "Device",
  utm_source: "UTM source",
  utm_medium: "UTM medium",
  utm_campaign: "UTM campaign",
};

const PALETTE = ["#00FF88", "#14F195", "#7BE495", "#FFD60A", "#FFB066", "#FF6B6B", "#A78BFA", "#C77DFF", "#7DD3FC"];

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="glass rounded-2xl p-5 flex items-center gap-4" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s/g,"-")}`}>
    <div className="w-12 h-12 rounded-xl bg-[#00FF88]/10 border border-[#00FF88]/20 grid place-items-center">
      <Icon className="w-5 h-5 text-[#00FF88]" />
    </div>
    <div>
      <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">{label}</div>
      <div className="font-display font-extrabold text-2xl">{value}</div>
      {sub && <div className="text-[11px] text-white/45 font-mono-c mt-0.5">{sub}</div>}
    </div>
  </div>
);

const BreakdownCard = ({ field, rows }) => {
  if (!rows || rows.length === 0) {
    return (
      <div className="glass rounded-2xl p-5" data-testid={`breakdown-${field}`}>
        <div className="font-mono-c text-[10px] uppercase tracking-widest text-[#00FF88] mb-2">{FIELD_LABELS[field] || field}</div>
        <div className="text-white/30 text-sm">No data yet</div>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.count));
  return (
    <div className="glass rounded-2xl p-5" data-testid={`breakdown-${field}`}>
      <div className="font-mono-c text-[10px] uppercase tracking-widest text-[#00FF88] mb-3">{FIELD_LABELS[field] || field}</div>
      <div className="space-y-2.5">
        {rows.slice(0, 8).map((r, i) => (
          <div key={r.value}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/80 truncate">{r.value}</span>
              <span className="font-mono-c text-white/50 shrink-0 ml-2">{r.count} · {r.percent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(r.count / max) * 100}%`,
                  background: i === 0 ? "linear-gradient(90deg, #00FF88, #14F195)" : "rgba(255,255,255,0.25)",
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist/analytics`, { headers: { "X-Admin-Token": token } });
      setData(res.data);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <div className="py-20 text-center text-white/40"><Loader2 className="w-6 h-6 animate-spin inline" /></div>;
  }
  if (!data) return null;

  const dailyData = (data.daily || []).map((d) => ({ date: d.date.slice(5), count: d.count }));
  const sourceData = (data.breakdowns.source || []).map((r, i) => ({ name: r.value, count: r.count, fill: PALETTE[i % PALETTE.length] }));

  return (
    <div data-testid="analytics-tab">
      {/* Growth Since Launch — investor highlight panel */}
      <div className="glass rounded-2xl p-6 mb-8 edge-glow-soft" data-testid="growth-since-launch">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88]">// Growth since launch</div>
            <div className="font-display font-extrabold text-2xl mt-1">Investor snapshot</div>
          </div>
          <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">live</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div data-testid="growth-total">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Total signups</div>
            <div className="font-display font-black text-3xl mt-1">{data.total.toLocaleString()}</div>
          </div>
          <div data-testid="growth-24h">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Last 24 hours</div>
            <div className="font-display font-black text-3xl mt-1 text-[#00FF88]">+{(data.today_count ?? 0).toLocaleString()}</div>
          </div>
          <div data-testid="growth-7d">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Last 7 days</div>
            <div className="font-display font-black text-3xl mt-1 text-[#00FF88]">+{(data.week_count ?? 0).toLocaleString()}</div>
            {data.week_growth_pct !== null && data.week_growth_pct !== undefined && (
              <div className="text-[11px] font-mono-c text-white/45 mt-0.5">{data.week_growth_pct >= 0 ? "+" : ""}{data.week_growth_pct}% WoW</div>
            )}
          </div>
          <div data-testid="growth-conversion">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Completion rate</div>
            <div className="font-display font-black text-3xl mt-1">{data.completion_rate ?? 0}%</div>
            <div className="text-[11px] font-mono-c text-white/45 mt-0.5">{(data.completed ?? 0).toLocaleString()} completed</div>
          </div>
          <div data-testid="growth-multi-app">
            <div className="text-[10px] font-mono-c uppercase tracking-widest text-white/40">Multi-app workers</div>
            <div className="font-display font-black text-3xl mt-1">{data.multi_app_rate ?? 0}%</div>
            <div className="text-[11px] font-mono-c text-white/45 mt-0.5">{(data.multi_app_users ?? 0).toLocaleString()} earners</div>
          </div>
        </div>
      </div>

      {/* Top stats */}
      <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users}      label="Total signups"        value={data.total.toLocaleString()} />
        <StatCard icon={CheckCircle} label="Completed onboarding" value={data.completed.toLocaleString()} sub={`${data.completion_rate}% completion`} />
        <StatCard icon={Sparkles}   label="High-intent users"    value={(data.high_intent ?? 0).toLocaleString()} sub={`${data.high_intent_rate ?? 0}% of total`} />
        <StatCard icon={Layers}     label="Multi-app users"      value={(data.multi_app_users ?? 0).toLocaleString()} sub={`${data.multi_app_rate ?? 0}% multi-app`} />
        <StatCard icon={TrendingUp} label="Signups today"        value={(data.today_count ?? 0).toLocaleString()} sub={`${dailyData.length} days tracked`} />
      </div>

      {/* Daily growth */}
      <div className="glass rounded-2xl p-5 mb-8" data-testid="daily-growth-chart">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-mono-c text-[10px] uppercase tracking-widest text-[#00FF88]">// Daily signups</div>
            <div className="font-display font-extrabold text-xl mt-0.5">Growth (last 30 days)</div>
          </div>
        </div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00FF88" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#00FF88" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(0,255,136,0.3)", borderRadius: 12, fontSize: 12 }}
                labelStyle={{ color: "#00FF88", fontFamily: "JetBrains Mono" }}
              />
              <Area type="monotone" dataKey="count" stroke="#00FF88" strokeWidth={2} fill="url(#grad1)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Traffic source chart */}
      {sourceData.length > 0 && (
        <div className="glass rounded-2xl p-5 mb-8" data-testid="source-chart">
          <div className="font-mono-c text-[10px] uppercase tracking-widest text-[#00FF88] mb-1">// Traffic sources</div>
          <div className="font-display font-extrabold text-xl mb-4">How earners find us</div>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sourceData} layout="vertical" margin={{ left: 30 }}>
                <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.6)" fontSize={12} tickLine={false} axisLine={false} width={90} />
                <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(0,255,136,0.3)", borderRadius: 12, fontSize: 12 }} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                  {sourceData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Breakdowns grid */}
      <div className="font-mono-c text-[10px] uppercase tracking-widest text-[#00FF88] mb-3">// Question breakdowns</div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {Object.keys(FIELD_LABELS).map((field) => (
          <BreakdownCard key={field} field={field} rows={data.breakdowns[field]} />
        ))}
      </div>

      <button
        onClick={load}
        className="mt-8 inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition-colors"
      >
        <RefreshCw className="w-4 h-4" /> Refresh analytics
      </button>
    </div>
  );
};

export default AnalyticsTab;

/* -------- Filter bar for the signups tab -------- */
export const FilterBar = ({ data, filters, setFilters }) => {
  const uniques = useMemo(() => {
    const get = (key) => Array.from(new Set(data.map((r) => r[key]).filter(Boolean))).sort();
    return {
      platform: get("platform"),
      earnings_range: get("earnings_range"),
      ideal_advance: get("ideal_advance"),
      top_challenge: get("top_challenge"),
      urgency: get("urgency"),
      membership_interest: get("membership_interest"),
      beta_interest: get("beta_interest"),
      interview_interest: get("interview_interest"),
      state: get("state"),
    };
  }, [data]);

  const FilterSelect = ({ field, label }) => (
    <select
      value={filters[field] || ""}
      onChange={(e) => setFilters((f) => ({ ...f, [field]: e.target.value }))}
      className="h-9 px-3 bg-[#0A0A0A] border border-white/10 rounded-full text-xs text-white/80 focus:outline-none focus:border-[#00FF88]/40 cursor-pointer"
      data-testid={`filter-${field}`}
    >
      <option value="">{label}: All</option>
      {(uniques[field] || []).map((v) => <option key={v} value={v}>{v}</option>)}
    </select>
  );

  const clear = () => setFilters({});
  const active = Object.values(filters).filter(Boolean).length;
  const hiOn = !!filters.high_intent;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="filter-bar">
      <Filter className="w-4 h-4 text-white/40" />
      <button
        onClick={() => setFilters((f) => ({ ...f, high_intent: hiOn ? "" : "yes" }))}
        className={`h-9 px-3.5 rounded-full text-xs font-bold border transition-all inline-flex items-center gap-1.5 ${
          hiOn
            ? "bg-[#00FF88] text-black border-[#00FF88]"
            : "bg-[#0A0A0A] text-white/80 border-white/10 hover:border-[#00FF88]/40"
        }`}
        data-testid="filter-high-intent"
      >
        <Sparkles className="w-3.5 h-3.5" />
        High intent only
      </button>
      <FilterSelect field="platform"            label="Platform" />
      <FilterSelect field="earnings_range"      label="Earnings" />
      <FilterSelect field="ideal_advance"       label="Advance" />
      <FilterSelect field="top_challenge"       label="Challenge" />
      <FilterSelect field="urgency"             label="Urgency" />
      <FilterSelect field="membership_interest" label="Membership" />
      <FilterSelect field="beta_interest"       label="Beta" />
      <FilterSelect field="interview_interest"  label="Interview" />
      <FilterSelect field="state"               label="State" />
      {active > 0 && (
        <button onClick={clear} className="text-xs text-[#00FF88] hover:text-white transition-colors font-mono-c">
          clear ({active})
        </button>
      )}
    </div>
  );
};
